import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Loader2, Warehouse } from 'lucide-react';
import { AnalysisWarehouseMaterialDialog } from './AnalysisWarehouseMaterialDialog';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { PipeSelector } from '../../../components/ui/PipeSelector';
import { SearchableSelectInDialog } from '../../../components/ui/SearchableSelectInDialog';
import { Notification } from '../../../components/ui/custom-notification';
import { AddMaterialDialog } from '../../project/components/dashboard/materials/AddMaterialDialog';
import { extractPipeSizesFromPipeDetails } from '../../project/components/dashboard/materials/extractPipeSizesFromPipeDetails';
import type { DashboardProject, Project } from '../../project/types/project';
import type { MaterialData } from '../../project/types/materials';
import { DocumentTag } from '../../project/types';
import { fetchProjectList } from '../../../redux/Project/action';
import { useUserPermissionsAfterLogin } from '../../admin/hooks/useUserPermissionsAfterLogin';
import { getListMaterialCategory } from '../../../redux/Project/materialsData/action';
import {
  clearAllPipeDetails,
  getAllPipeDetails,
} from '../../../redux/Project/setupData/action';

export type AnalysisAddFitterSeed = {
  projectId: string;
  pipeNo: string;
  pipePipeId?: string;
};

type AnalysisAddMaterialProps = {
  onMaterialAdded?: () => void;
  /** Hide Add Material / Add Project Material toolbar (Fitter browse uses + only). */
  hideToolbar?: boolean;
  /** Hide Add Project Material when user has no assigned project access (warehouse Add Material may still show). */
  showAddProjectMaterial?: boolean;
  /** Open Add Project Material seeded from a Fitter inventory row. */
  fitterSeed?: AnalysisAddFitterSeed | null;
  onFitterSeedConsumed?: () => void;
};

function formatProjectLabel(p: Project): string {
  return p.projectNumber ? `${p.projectName} (${p.projectNumber})` : p.projectName;
}

export const AnalysisAddMaterial: React.FC<AnalysisAddMaterialProps> = ({
  onMaterialAdded,
  hideToolbar = false,
  showAddProjectMaterial = true,
  fitterSeed = null,
  onFitterSeedConsumed,
}) => {
  const dispatch = useDispatch();
  const { canAccessAdminFeatures, userPermissions, userRole } = useUserPermissionsAfterLogin();
  const userId = userPermissions?.user_id ?? null;
  const restrictProjectsToUserAccess = userRole === 'user';
  const projectList = useSelector(
    (state: { project?: { ProjectList?: Project[] } }) => state.project?.ProjectList ?? []
  ) as Project[];
  const { pipeDetails, isPipeDetailsLoading } = useSelector(
    (state: { setupData?: { pipeDetails?: Record<string, unknown>; isPipeDetailsLoading?: boolean } }) =>
      state.setupData ?? {}
  );
  const materialCategories = useSelector(
    (state: { materialsData?: { materialCategories?: DocumentTag[] } }) =>
      state.materialsData?.materialCategories
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectLabel, setSelectedProjectLabel] = useState('');
  const [selectedPipeIndex, setSelectedPipeIndex] = useState(0);
  const [dashboardProject, setDashboardProject] = useState<DashboardProject | null>(null);
  const [pipeSizesData, setPipeSizesData] = useState<Array<Record<string, unknown>>>([]);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [fetchingProjectId, setFetchingProjectId] = useState<string | null>(null);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [activeSeed, setActiveSeed] = useState<AnalysisAddFitterSeed | null>(null);
  const [pendingFitterOpen, setPendingFitterOpen] = useState(false);
  const processedFitterSeedRef = useRef<string | null>(null);
  const onFitterSeedConsumedRef = useRef(onFitterSeedConsumed);
  onFitterSeedConsumedRef.current = onFitterSeedConsumed;

  useEffect(() => {
    dispatch(getListMaterialCategory({}));
  }, [dispatch]);

  // Fitter inventory + → load project pipes and open Add with seed (once per seed key).
  useEffect(() => {
    if (!fitterSeed?.projectId || !fitterSeed.pipeNo) return;
    const seedKey = `${fitterSeed.projectId}|${fitterSeed.pipeNo}|${fitterSeed.pipePipeId ?? ''}`;
    if (processedFitterSeedRef.current === seedKey) {
      onFitterSeedConsumedRef.current?.();
      return;
    }
    processedFitterSeedRef.current = seedKey;

    const proj = projectList.find((p) => String(p.projectId) === String(fitterSeed.projectId));
    if (!proj) {
      Notification.warning('Project not found', 'Could not open Add for this Fitter end.');
      onFitterSeedConsumedRef.current?.();
      return;
    }
    setActiveSeed(fitterSeed);
    setSelectedProjectId(String(proj.projectId));
    setSelectedProjectLabel(formatProjectLabel(proj));
    setDashboardProject({
      project: proj,
      team: [],
      documents: [],
      materials: [],
    });
    setPipeSizesData([]);
    setSelectedPipeIndex(0);
    setFetchingProjectId(String(proj.projectId));
    setPendingFitterOpen(true);
    dispatch(clearAllPipeDetails());
    dispatch(getAllPipeDetails({ projectId: proj.projectId }));
    onFitterSeedConsumedRef.current?.();
  }, [fitterSeed, projectList, dispatch]);

  useEffect(() => {
    if (!pendingFitterOpen || !dashboardProject || fetchingProjectId || isPipeDetailsLoading) return;
    if (!pipeSizesData.length) return;

    let index = 0;
    const seedPipeId = String(activeSeed?.pipePipeId ?? '').trim();
    if (seedPipeId) {
      const found = pipeSizesData.findIndex((p) => String(p.pipeId) === seedPipeId);
      if (found >= 0) index = found;
    }
    setSelectedPipeIndex(index);
    setAddDialogOpen(true);
    setPendingFitterOpen(false);
  }, [
    pendingFitterOpen,
    dashboardProject,
    fetchingProjectId,
    isPipeDetailsLoading,
    pipeSizesData,
    activeSeed,
  ]);

  useEffect(() => {
    if (!restrictProjectsToUserAccess || !userId) return;
    dispatch(fetchProjectList({ userId }));
  }, [dispatch, restrictProjectsToUserAccess, userId]);

  useEffect(() => {
    if (restrictProjectsToUserAccess) return;
    if (projectList.length > 0) return;
    if (canAccessAdminFeatures()) {
      dispatch(fetchProjectList({}));
    } else if (userId) {
      dispatch(fetchProjectList({ userId }));
    }
  }, [
    dispatch,
    projectList.length,
    canAccessAdminFeatures,
    userId,
    restrictProjectsToUserAccess,
  ]);

  useEffect(() => {
    if (materialCategories && materialCategories.length > 0) {
      const uniqueTags = materialCategories
        .filter((tag, index, self) => index === self.findIndex((t) => t.tags_name === tag.tags_name))
        .sort((a, b) => a.tags_name.localeCompare(b.tags_name));
      setDocumentTags(uniqueTags);
    }
  }, [materialCategories]);

  const projectOptions = useMemo(
    () =>
      projectList.map((p) => ({
        id: String(p.projectId),
        name: formatProjectLabel(p),
        searchText: `${p.projectName} ${p.projectNumber ?? ''} ${p.projectId}`.trim(),
      })),
    [projectList]
  );

  const pipeOptions = useMemo(
    () =>
      pipeSizesData.map((pipe, index) => ({
        value: String(index),
        label: String(pipe.displayName ?? '').trim(),
      })),
    [pipeSizesData]
  );

  const resetPickerState = useCallback(() => {
    setSelectedProjectId('');
    setSelectedProjectLabel('');
    setSelectedPipeIndex(0);
    setDashboardProject(null);
    setPipeSizesData([]);
    setFetchingProjectId(null);
  }, []);

  const handleOpenPicker = () => {
    resetPickerState();
    setPickerOpen(true);
  };

  const handleProjectSelect = (projectLabel: string) => {
    setSelectedProjectLabel(projectLabel);
    setSelectedPipeIndex(0);
    setPipeSizesData([]);

    const proj = projectList.find((p) => formatProjectLabel(p) === projectLabel);
    if (!proj) {
      Notification.warning('Project not found', 'Could not load the selected project.');
      return;
    }

    const projectId = String(proj.projectId);
    setSelectedProjectId(projectId);
    setDashboardProject({
      project: proj,
      team: [],
      documents: [],
      materials: [],
    });
    setFetchingProjectId(projectId);
    dispatch(clearAllPipeDetails());
    dispatch(getAllPipeDetails({ projectId: proj.projectId }));
  };

  useEffect(() => {
    if (!dashboardProject || !fetchingProjectId || isPipeDetailsLoading) return;
    if (String(dashboardProject.project.projectId) !== fetchingProjectId) return;
    if (!pipeDetails || Object.keys(pipeDetails).length === 0) return;

    const extracted = extractPipeSizesFromPipeDetails(
      pipeDetails as Record<string, unknown>,
      dashboardProject.project
    );
    setPipeSizesData(extracted);
    setFetchingProjectId(null);
    if (extracted.length === 0) {
      Notification.warning('No pipes found', 'This project has no pipe setup entries yet.');
    }
  }, [
    pipeDetails,
    isPipeDetailsLoading,
    dashboardProject,
    fetchingProjectId,
  ]);

  const handleContinueToAddMaterial = () => {
    if (!dashboardProject) {
      Notification.warning('Select a project', 'Choose a project to continue.');
      return;
    }
    if (!pipeSizesData.length || !pipeSizesData[selectedPipeIndex]) {
      Notification.warning('Select a pipe', 'Choose a pipe size / pipe ID to continue.');
      return;
    }
    const pipe = pipeSizesData[selectedPipeIndex];
    if (!pipe.materialTraceability) {
      Notification.warning(
        'Material traceability disabled',
        'Material traceability is not enabled for this pipe setup.'
      );
      return;
    }
    setPickerOpen(false);
    setAddDialogOpen(true);
  };

  const handleAddMaterialComplete = async (_material: MaterialData) => {
    onMaterialAdded?.();
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setActiveSeed(null);
    processedFitterSeedRef.current = null;
  };

  const unit = dashboardProject?.project.unit || 'in';

  return (
    <>
      {!hideToolbar && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-hero-red text-white hover:bg-hero-red-60 gap-2"
            onClick={() => setWarehouseDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Material
          </Button>
          {showAddProjectMaterial ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={handleOpenPicker}
            >
              <Warehouse className="h-4 w-4" />
              Add Project Material
            </Button>
          ) : null}
        </div>
      )}

      <AnalysisWarehouseMaterialDialog
        isOpen={warehouseDialogOpen}
        onClose={() => setWarehouseDialogOpen(false)}
        onSaved={() => onMaterialAdded?.()}
        documentTags={documentTags}
      />

      <Dialog
        open={pickerOpen}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (!open) resetPickerState();
        }}
      >
        <DialogContent className="max-w-lg overflow-visible">
          <DialogHeader>
            <DialogTitle>Add material</DialogTitle>
            <p className="text-sm text-heritage-grey">
              Select the project and pipe size for adding material.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Project</Label>
              <SearchableSelectInDialog
                value={selectedProjectLabel || undefined}
                onValueChange={handleProjectSelect}
                placeholder="Select project..."
                searchPlaceholder="Search projects..."
                options={projectOptions}
              />
            </div>

            <div className="space-y-2">
              <Label>Pipe size</Label>
              {isPipeDetailsLoading || fetchingProjectId ? (
                <div className="flex items-center gap-2 text-sm text-heritage-grey py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading pipes for project…
                </div>
              ) : (
                <PipeSelector
                  triggerWidth="w-full"
                  value={String(selectedPipeIndex)}
                  onChange={(value) => setSelectedPipeIndex(Number(value))}
                  placeholder={
                    selectedProjectId ? 'Select pipe' : 'Select a project first'
                  }
                  options={pipeOptions}
                />
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setPickerOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleContinueToAddMaterial}
              disabled={
                !selectedProjectId ||
                isPipeDetailsLoading ||
                !!fetchingProjectId ||
                pipeOptions.length === 0
              }
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dashboardProject && addDialogOpen && pipeSizesData.length > 0 && (
        <AddMaterialDialog
          isOpen={addDialogOpen}
          onClose={handleCloseAddDialog}
          onAdd={handleAddMaterialComplete}
          selectedPipeSize={selectedPipeIndex}
          onPipeSizeChange={setSelectedPipeIndex}
          project={dashboardProject}
          pipeSizesData={pipeSizesData as any[]}
          unit={unit}
          setupData={{}}
          documentTags={documentTags}
          setDocumentTags={setDocumentTags}
          pipeInventoryMode
          seedPipeNo={activeSeed?.pipeNo}
          seedPipePipeId={activeSeed?.pipePipeId}
        />
      )}
    </>
  );
};
